import React, { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Check, X, Flame, Trash2, ShieldCheck, UserMinus, Undo2 } from "lucide-react";

export default function Admin() {
  const [stats, setStats] = useState(null);
  const [tab, setTab] = useState("pending");
  const [subs, setSubs] = useState([]);
  const [slang, setSlang] = useState([]);
  const [users, setUsers] = useState([]);
  const [me, setMe] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadStats = useCallback(async () => {
    const { data } = await api.get("/admin/stats");
    setStats(data);
  }, []);

  const loadSubs = useCallback(async (status) => {
    setLoading(true);
    const { data } = await api.get("/admin/submissions", { params: { status } });
    setSubs(data.items);
    setLoading(false);
  }, []);

  const loadSlang = useCallback(async () => {
    setLoading(true);
    const { data } = await api.get("/slang", { params: { limit: 500 } });
    setSlang(data.items);
    setLoading(false);
  }, []);

  const loadUsers = useCallback(async () => {
    setLoading(true);
    const [u, meRes] = await Promise.all([api.get("/admin/users"), api.get("/auth/me")]);
    setUsers(u.data.items);
    setMe(meRes.data.user);
    setLoading(false);
  }, []);

  useEffect(() => { loadStats(); }, [loadStats]);
  useEffect(() => {
    if (tab === "slang") loadSlang();
    else if (tab === "users") loadUsers();
    else loadSubs(tab);
  }, [tab, loadSubs, loadSlang, loadUsers]);

  const review = async (id, action) => {
    try {
      await api.post(`/admin/submissions/${id}/review`, { action });
      toast.success(`Submission ${action}d`);
      await Promise.all([loadStats(), loadSubs(tab)]);
    } catch (e) {
      toast.error("Failed");
    }
  };

  const revoke = async (id, word) => {
    const reason = window.prompt(`Revoke "${word}" — remove it from the public dictionary?\n\nOptional reason (sent to the submitter):`, "");
    if (reason === null) return;
    try {
      await api.post(`/admin/submissions/${id}/revoke`, { action: "reject", reason });
      toast.success(`Revoked ${word}`);
      await Promise.all([loadStats(), loadSubs(tab)]);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  const toggleTrending = async (id, current) => {
    try {
      await api.patch(`/admin/slang/${id}/trending`, { trending: !current });
      setSlang((items) => items.map((s) => s.id === id ? { ...s, trending: !current } : s));
      toast.success(`Updated trending`);
    } catch { toast.error("Failed"); }
  };

  const removeSlang = async (id) => {
    if (!window.confirm("Delete this word from the dex?")) return;
    try {
      await api.delete(`/admin/slang/${id}`);
      setSlang((items) => items.filter((s) => s.id !== id));
      toast.success("Deleted");
      loadStats();
    } catch { toast.error("Failed"); }
  };

  const updateRole = async (id, role, email) => {
    const verb = role === "admin" ? "promote to admin" : "demote to user";
    if (!window.confirm(`${verb.charAt(0).toUpperCase() + verb.slice(1)}: ${email}?`)) return;
    try {
      await api.patch(`/admin/users/${id}/role`, { role });
      setUsers((items) => items.map((u) => u.id === id ? { ...u, role } : u));
      toast.success(`Updated ${email} -> ${role}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-8 py-10">
      <h1 className="font-display text-5xl tracking-tighter mb-2">think tank<span className="text-hot">.</span></h1>
      <p className="font-mono-tag text-xs uppercase mb-6">vet submissions · curate trending</p>

      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-8">
          <Stat label="Words" value={stats.total_slang} bg="bg-lime" testId="stat-total-slang" />
          <Stat label="Pending" value={stats.pending_submissions} bg="bg-peach" testId="stat-pending" />
          <Stat label="Approved" value={stats.approved_submissions} bg="bg-mint" testId="stat-approved" />
          <Stat label="Users" value={stats.total_users} bg="bg-lilac" testId="stat-users" />
        </div>
      )}

      <div className="flex gap-2 flex-wrap mb-6">
        {["pending", "approved", "rejected", "slang", "users"].map((t) => (
          <button
            key={t}
            data-testid={`admin-tab-${t}`}
            onClick={() => setTab(t)}
            className={`font-display uppercase text-sm px-4 py-2 brutal-sm brutal-hover ${tab === t ? "bg-hot text-white" : "bg-white"}`}
          >
            {t === "slang" ? "All slang" : t}
          </button>
        ))}
      </div>

      {loading ? (
        <p className="font-mono-tag uppercase">loading...</p>
      ) : tab === "users" ? (
        <div className="space-y-3">
          {users.map((u) => (
            <div key={u.id} data-testid={`admin-user-${u.id}`} className="bg-white brutal p-4 flex flex-wrap items-center justify-between gap-3">
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <h4 className="font-display text-2xl leading-none truncate">{u.name || u.email}</h4>
                  <span className={`font-mono-tag text-[10px] uppercase brutal-sm px-2 py-0.5 ${u.role === "admin" ? "bg-lilac" : "bg-altsoft"}`}>{u.role}</span>
                </div>
                <p className="text-sm mt-1">{u.email}</p>
                <p className="font-mono-tag text-[10px] uppercase mt-1">
                  joined {u.created_at ? new Date(u.created_at).toLocaleDateString() : "—"} · {u.submissions_total} submissions · {u.submissions_approved} approved
                </p>
              </div>
              {me && me.id !== u.id && (
                <div className="flex gap-2 shrink-0">
                  {u.role === "user" ? (
                    <button
                      data-testid={`promote-${u.id}`}
                      onClick={() => updateRole(u.id, "admin", u.email)}
                      className="bg-lilac brutal-sm px-3 py-2 font-display uppercase text-sm brutal-hover brutal-active inline-flex items-center gap-1"
                    >
                      <ShieldCheck className="h-4 w-4" strokeWidth={3} /> Promote
                    </button>
                  ) : (
                    <button
                      data-testid={`demote-${u.id}`}
                      onClick={() => updateRole(u.id, "user", u.email)}
                      className="bg-white brutal-sm px-3 py-2 font-display uppercase text-sm brutal-hover brutal-active inline-flex items-center gap-1"
                    >
                      <UserMinus className="h-4 w-4" strokeWidth={3} /> Demote
                    </button>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      ) : tab === "slang" ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {slang.map((s) => (
            <div key={s.id} data-testid={`admin-slang-${s.id}`} className="bg-white brutal-sm p-4 flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <h4 className="font-display text-2xl leading-none">{s.word}</h4>
                  <span className="font-mono-tag text-[10px] uppercase bg-altsoft brutal-sm px-2 py-0.5">{s.category}</span>
                </div>
                <p className="text-xs line-clamp-2">{s.meaning}</p>
              </div>
              <div className="flex flex-col gap-2 shrink-0">
                <button
                  data-testid={`toggle-trending-${s.id}`}
                  onClick={() => toggleTrending(s.id, s.trending)}
                  className={`p-2 brutal-sm brutal-hover ${s.trending ? "bg-hot text-white" : "bg-white"}`}
                  title="Toggle trending"
                >
                  <Flame className="h-4 w-4" strokeWidth={3} />
                </button>
                <button
                  data-testid={`delete-slang-${s.id}`}
                  onClick={() => removeSlang(s.id)}
                  className="p-2 brutal-sm brutal-hover bg-white"
                  title="Delete"
                >
                  <Trash2 className="h-4 w-4" strokeWidth={3} />
                </button>
              </div>
            </div>
          ))}
        </div>
      ) : subs.length === 0 ? (
        <p data-testid="admin-empty" className="font-mono-tag uppercase">nothing here</p>
      ) : (
        <div className="space-y-3">
          {subs.map((s) => (
            <div key={s.id} data-testid={`admin-submission-${s.id}`} className="bg-white brutal p-5">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <h3 className="font-display text-3xl leading-none">{s.word}</h3>
                  <p className="font-mono-tag text-[10px] uppercase mt-1">by {s.user_email} · {s.category}</p>
                </div>
                {tab === "pending" && (
                  <div className="flex gap-2">
                    <button
                      data-testid={`approve-${s.id}`}
                      onClick={() => review(s.id, "approve")}
                      className="bg-lime brutal-sm px-3 py-2 font-display uppercase text-sm brutal-hover brutal-active inline-flex items-center gap-1"
                    >
                      <Check className="h-4 w-4" strokeWidth={3} /> Approve
                    </button>
                    <button
                      data-testid={`reject-${s.id}`}
                      onClick={() => review(s.id, "reject")}
                      className="bg-hot text-white brutal-sm px-3 py-2 font-display uppercase text-sm brutal-hover brutal-active inline-flex items-center gap-1"
                    >
                      <X className="h-4 w-4" strokeWidth={3} /> Reject
                    </button>
                  </div>
                )}
                {tab === "approved" && (
                  <button
                    data-testid={`revoke-${s.id}`}
                    onClick={() => revoke(s.id, s.word)}
                    className="bg-hot text-white brutal-sm px-3 py-2 font-display uppercase text-sm brutal-hover brutal-active inline-flex items-center gap-1"
                  >
                    <Undo2 className="h-4 w-4" strokeWidth={3} /> Revoke
                  </button>
                )}
              </div>
              <p className="mt-3 text-sm">{s.meaning}</p>
              {s.example && <p className="font-hand text-lg mt-1">"{s.example}"</p>}
              {s.ai_verdict && (
                <div className={`mt-3 brutal-sm p-3 ${s.ai_verdict.approved ? "bg-mint" : "bg-peach"}`}>
                  <p className="font-mono-tag text-[10px] uppercase mb-1">
                    AI verdict · {s.ai_verdict.approved ? "approve" : "reject"} · conf {(s.ai_verdict.confidence || 0).toFixed(2)}
                  </p>
                  <p className="text-sm">{s.ai_verdict.reason}</p>
                </div>
              )}
              {s.review_reason && (
                <p className="mt-2 font-mono-tag text-[10px] uppercase">admin note: {s.review_reason}</p>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function Stat({ label, value, bg, testId }) {
  return (
    <div data-testid={testId} className={`${bg} brutal p-4`}>
      <p className="font-mono-tag text-[10px] uppercase">{label}</p>
      <p className="font-display text-4xl leading-none mt-1">{value}</p>
    </div>
  );
}
